import { useState, useEffect, useCallback, useRef } from 'react';

interface UseFullPageScrollProps {
  totalSections: number;
  debounceDelay?: number;
}

export const useFullPageScroll = ({ 
  totalSections, 
  debounceDelay = 1000 
}: UseFullPageScrollProps) => {
  const [currentSection, setCurrentSection] = useState(0);
  const isScrollingRef = useRef(false);
  const touchStartRef = useRef(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const goToSection = useCallback((sectionIndex: number) => {
    if (sectionIndex >= 0 && sectionIndex < totalSections && !isScrollingRef.current) {
      isScrollingRef.current = true;
      setCurrentSection(sectionIndex);
      
      setTimeout(() => {
        isScrollingRef.current = false;
      }, debounceDelay);
    }
  }, [totalSections, debounceDelay]);

  const handleNext = useCallback(() => {
    if (currentSection < totalSections - 1) {
      goToSection(currentSection + 1);
    }
  }, [currentSection, totalSections, goToSection]);

  const handlePrev = useCallback(() => {
    if (currentSection > 0) {
      goToSection(currentSection - 1);
    }
  }, [currentSection, goToSection]);

  useEffect(() => {
    // Disable full-page scroll on mobile
    if (isMobile) return;

    const handleWheel = (e: WheelEvent) => {
      if (isScrollingRef.current) return;

      const target = e.target as HTMLElement;
      const scrollableSection = target.closest('.fullpage-section-scrollable');
      
      if (scrollableSection) {
        const isAtTop = scrollableSection.scrollTop === 0;
        const isAtBottom = scrollableSection.scrollTop + scrollableSection.clientHeight >= scrollableSection.scrollHeight - 1;
        
        if ((e.deltaY < 0 && !isAtTop) || (e.deltaY > 0 && !isAtBottom)) {
          return;
        }
      }
      
      e.preventDefault();

      if (e.deltaY > 0) {
        handleNext();
      } else if (e.deltaY < 0) {
        handlePrev();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isScrollingRef.current) return;

      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          handlePrev();
          break;
        case 'Home':
          e.preventDefault();
          goToSection(0);
          break;
        case 'End':
          e.preventDefault();
          goToSection(totalSections - 1);
          break;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStartRef.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isScrollingRef.current) return;

      const touchEnd = e.touches[0].clientY;
      const diff = touchStartRef.current - touchEnd;

      if (Math.abs(diff) > 50) {
        e.preventDefault();
        if (diff > 0) {
          handleNext();
        } else {
          handlePrev();
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleNext, handlePrev, goToSection, totalSections, isMobile]);

  return {
    currentSection,
    goToSection,
    isScrolling: isScrollingRef.current,
  };
};
